import type { PhonicsAudio } from "@/generated/graphql";
import { Button } from "@ai-teach/ui-web";
import { useCallback, useEffect, useRef, useState } from "react";
import { FaPause, FaPlay, FaTrash } from "react-icons/fa";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";

interface VoiceVariantCardProps {
  audio: PhonicsAudio;
  isPlaying: boolean;
  onTogglePlay: () => void;
  onAdd?: () => void;
  copying?: boolean;
  // admin props — when provided, admin controls are shown
  onDelete?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  isFirst?: boolean;
  isLast?: boolean;
  deleting?: boolean;
}

const formatTime = (seconds: number): string => {
  if (!Number.isFinite(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

export function VoiceVariantCard({
  audio,
  isPlaying,
  onTogglePlay,
  onAdd,
  copying,
  onDelete,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  deleting,
}: VoiceVariantCardProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const isAdminMode = !!onDelete;

  useEffect(() => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.play();
    } else {
      audioRef.current.pause();
    }
  }, [isPlaying]);

  const handleAudioEnded = () => {
    setCurrentTime(0);
    onTogglePlay();
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current || isDragging) return;
    setCurrentTime(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration);
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = percent * duration;
    setCurrentTime(audioRef.current.currentTime);
  };

  const handleMouseDown = () => setIsDragging(true);

  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  useEffect(() => {
    if (!isDragging) return;
    window.addEventListener("mouseup", handleMouseUp);
    return () => window.removeEventListener("mouseup", handleMouseUp);
  }, [isDragging, handleMouseUp]);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="p-3 border rounded-lg bg-white hover:bg-slate-50 transition-colors">
      <div className="flex items-center gap-3 mb-2">
        {/* Reorder controls (admin only) */}
        {isAdminMode && (
          <div className="flex flex-col gap-0.5">
            <button
              type="button"
              onClick={onMoveUp}
              disabled={isFirst}
              className="p-0.5 rounded text-slate-400 hover:text-slate-700 disabled:opacity-25 disabled:cursor-not-allowed"
              title="Move up"
            >
              <HiChevronUp size={14} />
            </button>
            <button
              type="button"
              onClick={onMoveDown}
              disabled={isLast}
              className="p-0.5 rounded text-slate-400 hover:text-slate-700 disabled:opacity-25 disabled:cursor-not-allowed"
              title="Move down"
            >
              <HiChevronDown size={14} />
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={onTogglePlay}
          className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-primary-600 text-white hover:bg-primary-700 transition-colors"
        >
          {isPlaying ? <FaPause size={14} /> : <FaPlay size={14} className="ml-0.5" />}
        </button>

        {/* biome-ignore lint/a11y/useMediaCaption: phonics audio is pronunciation teaching, doesn't need a caption */}
        <audio
          ref={audioRef}
          src={audio.url}
          onEnded={handleAudioEnded}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="font-medium text-sm">Voice {audio.voiceVariant}</p>
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                audio.label === "sound"
                  ? "bg-green-100 text-green-700"
                  : "bg-slate-100 text-slate-500"
              }`}
            >
              {audio.label === "sound" ? "sound only" : "card"}
            </span>
          </div>
          <p className="text-xs text-slate-500 truncate">{audio.filename}</p>
        </div>

        {/* Add to Slide (read mode) */}
        {onAdd && (
          <Button onClick={onAdd} disabled={copying} size="sm" className="flex-shrink-0">
            Add to Slide
          </Button>
        )}

        {/* Delete (admin mode) */}
        {isAdminMode && (
          <button
            type="button"
            onClick={onDelete}
            disabled={deleting}
            className="flex-shrink-0 p-2 text-slate-400 hover:text-red-500 transition-colors disabled:opacity-50"
            title="Delete audio"
          >
            <FaTrash size={12} />
          </button>
        )}
      </div>

      {/* Progress Bar */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-500 font-mono w-10 text-right">
          {formatTime(currentTime)}
        </span>

        <div
          className="flex-1 h-2 bg-slate-200 rounded-full cursor-pointer relative group"
          onClick={handleSeek}
          onMouseDown={handleMouseDown}
        >
          <div
            className="absolute top-0 left-0 h-full bg-primary-600 rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
          <div className="absolute inset-0 bg-primary-400 opacity-0 group-hover:opacity-20 rounded-full transition-opacity" />
          <div
            className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white border-2 border-primary-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ left: `calc(${progress}% - 6px)` }}
          />
        </div>

        <span className="text-xs text-slate-500 font-mono w-10">{formatTime(duration)}</span>
      </div>
    </div>
  );
}
